import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, PenLine } from "lucide-react";
import { StarPicker } from "@/components/StarPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROPERTIES = [
  ["de-la-vie", "De La Vie Maison"],
  ["heritage", "Heritage"],
  ["hearthstead", "Hearthstead Cottage"],
];

const VALID = new Set(PROPERTIES.map((p) => p[0]));

export default function ReviewPage() {
  const [form, setForm] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    const residence = q.get("residence");
    return {
      name: (q.get("name") || "").slice(0, 80),
      property: VALID.has(residence) ? residence : "de-la-vie",
      rating: 5,
      quote: "",
    };
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/reviews`, form);
      setSent(true);
    } catch {
      toast.error("Something went wrong sending your review. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#16301f]/25 focus:border-[#16301f] outline-none py-2.5 text-sm text-[#16301f] placeholder:text-[#16301f]/35 transition-colors duration-300";
  const labelCls = "block text-[10px] uppercase tracking-[0.25em] text-[#16301f]/60 mb-1";

  return (
    <div data-testid="review-page" className="min-h-screen bg-[#f7f2e7] text-[#16301f]">
      <div className="max-w-xl mx-auto px-5 py-12 sm:py-16">
        <div className="text-center mb-10">
          <img src="/images/logo.png" alt="Parker Furnished Stays seal" className="w-20 h-20 mx-auto rounded-full" />
          <p className="text-xs uppercase tracking-[0.3em] text-[#16301f]/60 mt-6 flex items-center justify-center gap-4">
            <span className="h-px w-10 bg-[#16301f]/40 inline-block" />
            Guest Words
            <span className="h-px w-10 bg-[#16301f]/40 inline-block" />
          </p>
          <h1 className="font-display font-medium text-4xl sm:text-5xl mt-5">Share your stay</h1>
          <p className="font-script text-3xl text-[#1a3324] mt-3 -rotate-1 inline-block">
            A few kind words go a long way.
          </p>
          <p className="text-sm text-[#16301f]/70 font-light mt-6 max-w-md mx-auto">
            Thank you for staying with us. Tell future guests what your time in the residence was like —
            your review is shared with our host before it appears on the website.
          </p>
        </div>

        <div className="border border-[#16301f]/15 bg-[#fbf8f0] p-6 sm:p-10">
          {sent ? (
            <div data-testid="review-success" className="text-center py-12">
              <span className="mx-auto w-14 h-14 rounded-full border border-[#16301f] flex items-center justify-center">
                <Check className="w-6 h-6 text-[#16301f]" />
              </span>
              <h2 className="font-display text-3xl mt-6">Thank you{form.name ? `, ${form.name.split(" ")[0]}` : ""}</h2>
              <p className="text-sm text-[#16301f]/70 font-light mt-3 max-w-sm mx-auto">
                Your review has been sent to our host and will appear on the website once it's been read.
                We hope to welcome you back soon.
              </p>
              <a
                data-testid="review-back-home"
                href="/"
                className="inline-block mt-8 text-xs uppercase tracking-[0.22em] text-[#16301f]/70 border-b border-[#16301f]/40 pb-1 hover:text-[#16301f] hover:border-[#16301f] transition-colors duration-300"
              >
                Back to website
              </a>
            </div>
          ) : (
            <form data-testid="review-form" onSubmit={handleSubmit} className="space-y-7">
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                <div>
                  <label htmlFor="rv-name" className={labelCls}>Your Name *</label>
                  <input id="rv-name" data-testid="review-name-input" required maxLength={80} value={form.name} onChange={update("name")} placeholder="First name is fine" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="rv-property" className={labelCls}>Residence</label>
                  <select id="rv-property" data-testid="review-property-select" value={form.property} onChange={update("property")} className={`${inputCls} cursor-pointer`}>
                    {PROPERTIES.map(([id, name]) => (
                      <option key={id} value={id}>{name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <p className={labelCls}>Your rating</p>
                <StarPicker value={form.rating} onChange={(rating) => setForm((f) => ({ ...f, rating }))} testId="review-star-picker" />
              </div>
              <div>
                <label htmlFor="rv-quote" className={labelCls}>Your review *</label>
                <textarea id="rv-quote" data-testid="review-quote-input" required minLength={3} maxLength={1200} rows={5} value={form.quote} onChange={update("quote")} placeholder="What made your stay special?" className={`${inputCls} resize-none`} />
              </div>
              <button
                data-testid="review-submit-btn"
                type="submit"
                disabled={sending}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#16301f] text-[#f7f2e7] text-xs uppercase tracking-[0.25em] px-6 py-4 hover:bg-[#284b35] disabled:opacity-60 transition-colors duration-300"
              >
                <PenLine className="w-4 h-4" strokeWidth={1.5} />
                {sending ? "Sending..." : "Send Review"}
              </button>
            </form>
          )}
        </div>

        <p className="text-center mt-10">
          <a data-testid="review-back-link" href="/" className="text-xs uppercase tracking-[0.22em] text-[#16301f]/50 hover:text-[#16301f] transition-colors duration-300">
            ← Back to website
          </a>
        </p>
      </div>
    </div>
  );
}
